/* cspell:ignore labelledby */
import { esc } from '../../shared/rendering.js'
import type { KnowledgeSearchResult, PackDetails, ProjectPackSummary } from '../../api/knowledge.js'

export function renderPacksView(args: {
  readonly packs: readonly ProjectPackSummary[]
  readonly selected: PackDetails | null
  readonly searchQuery: string
  readonly searchResults: readonly KnowledgeSearchResult[]
  readonly loading?: boolean
  readonly error?: string | null
}): string {
  return `<div class="packs-page">
    <header class="page-header">
      <div>
        <p class="eyebrow">Knowledge Registry</p>
        <h1>Knowledge Packs</h1>
        <p class="muted">Attach, inspect, and search project-resolved engineering packs alongside the KB.</p>
      </div>
    </header>
    <section class="pack-search-panel" aria-labelledby="pack-search-title">
      <h2 id="pack-search-title">Unified search</h2>
      <form id="pack-search-form" class="pack-search-form">
        <label class="sr-only" for="pack-search-input">Search KB and packs</label>
        <input id="pack-search-input" name="q" value="${esc(args.searchQuery)}" placeholder="Search KB + packs" autocomplete="off" />
        <button type="submit">Search</button>
      </form>
      ${args.error ? `<p class="pack-error" role="alert">${esc(args.error)}</p>` : ''}
      ${args.loading ? '<p class="muted">Loading…</p>' : renderSearchResults(args.searchResults, args.searchQuery)}
    </section>
    <div class="packs-grid">
      <section class="pack-list" aria-labelledby="pack-list-title">
        <h2 id="pack-list-title">Attached packs</h2>
        ${renderPackRows(args.packs, args.selected?.packSlug ?? null)}
      </section>
      <section class="pack-detail" aria-labelledby="pack-detail-title">
        ${renderPackDetails(args.selected)}
      </section>
    </div>
  </div>`
}

function renderPackRows(packs: readonly ProjectPackSummary[], selectedSlug: string | null): string {
  if (packs.length === 0) return '<p class="muted">No packs attached to this project yet.</p>'
  return packs
    .map((pack) => {
      const selected = pack.packSlug === selectedSlug
      return `<button class="pack-row${selected ? ' is-selected' : ''}" data-pack-slug="${esc(pack.packSlug)}" type="button" aria-pressed="${selected}">
        <span><strong>${esc(pack.packSlug)}</strong><small>${esc(pack.kind)} · ${esc(pack.resolutionReason)}</small></span>
        <span class="pack-version">${esc(pack.activeVersion)}${pack.stale ? ' stale' : ''}</span>
      </button>`
    })
    .join('')
}

function renderPackDetails(pack: PackDetails | null): string {
  if (!pack)
    return '<h2 id="pack-detail-title">Pack details</h2><p class="muted">Select a pack to inspect versions and tags.</p>'
  const tags = pack.tags.map((tag) => `<span class="pack-tag">${esc(tag)}</span>`).join('')
  const versions = pack.versions
    .map(
      (version) =>
        `<li><code>${esc(version.version)}</code> <span>${esc(version.status)}</span></li>`,
    )
    .join('')
  return `<h2 id="pack-detail-title">${esc(pack.packName)}</h2>
    <p class="muted">${esc(pack.packSlug)} · ${esc(pack.kind)} · ${esc(pack.scope)}</p>
    ${pack.description ? `<p>${esc(pack.description)}</p>` : ''}
    <div class="pack-tags">${tags || '<span class="muted">No tags</span>'}</div>
    <h3>Versions</h3>
    <ul class="pack-versions">${versions || '<li>No versions indexed.</li>'}</ul>`
}

function renderSearchResults(results: readonly KnowledgeSearchResult[], query: string): string {
  if (!query.trim()) return '<p class="muted">Search results will appear here.</p>'
  if (results.length === 0) return '<p class="muted">No KB entries or pack chunks matched.</p>'
  return `<div class="pack-search-results">${results
    .map((result) => {
      const label =
        result.source === 'pack'
          ? `${result.packSlug ?? 'pack'}@${result.packVersion ?? '?'}`
          : 'KB'
      return `<article class="pack-search-result">
        <div><span class="pack-source">${esc(label)}</span><strong>${esc(result.title)}</strong></div>
        <p>${esc(result.preview)}</p>
        <code>${esc(result.id)}</code>
      </article>`
    })
    .join('')}</div>`
}
